/**
 * Tagging's Browse tab: every tagged clip as one row, a page at a time, newest first until a header
 * says otherwise. Name, length and the star rating come first, then one column per brand category in its settings order,
 * number one leftmost, each cell the values that category holds on the clip, and subclips and uploaded
 * last. Every header sorts and a second click flips it; a row click opens the clip in Tag clip; ticked
 * rows tag together from the top bar. A cell's pencil, shown on hover even when the cell is empty, edits
 * that category on that clip in place. With Show subclips on, each clip's kept subclips follow it,
 * indented, in timeline order, their cells editable the same way; a subclip row opens its clip in Tag
 * clip with that subclip selected. Above it, the Library's search, a filter for each of the brand's
 * first few categories and one for the creator, and the pager: where the page sits in the total, back
 * and forward, and a box to type a page number. Each clip and subclip carries its star rating.
 */
import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { NAMED_PEOPLE_FACET, TAGGED_FILTER_FACETS, TYPEAHEAD_DEBOUNCE_MS } from '@studio/contracts';
import { CellTagsModal, type EditTarget } from '@/components/cell-tags-modal';
import { Icon } from '@/components/icons';
import { Pager, pageCount } from '@/components/pager';
import { RenameClipModal } from '@/components/rename-clip-modal';
import { StarRating } from '@/components/star-rating';
import { useRatings } from '@/hooks/use-ratings';
import { fmtWhen } from '@/lib/format';
import { trpc } from '@/lib/trpc';

/** A detail column's key, or a category's id. */
type Sort = 'name' | 'length' | 'snippets' | 'created' | (string & {});
/** Text reads A to Z first; numbers and dates biggest and newest first. */
const TEXT_SORTS: Sort[] = ['name'];

/** The ticked rows live with the page, whose top bar carries their Tag button as the Library's does. */
export function TaggedTable({ onOpen, subclips, picked, setPicked }: {
  onOpen: (assetId: string, snippetId?: string) => void;
  subclips: boolean;
  picked: Set<string>;
  setPicked: (next: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
}) {
  const [sort, setSort] = useState<Sort>('created');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const { me } = useRouteContext({ from: '/_app' });
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  /** The cell whose category is being edited in place. */
  const [editing, setEditing] = useState<{ target: EditTarget; facetId: string } | null>(null);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  /** One chosen value per filtered category, by category id. */
  const [filters, setFilters] = useState<Record<string, string>>({});

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), TYPEAHEAD_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const brandFacets = useQuery(trpc.facets.list.queryOptions());
  // The first few categories carrying tags, then Creator: the people who made the clips are its values.
  const inUse = (brandFacets.data ?? [])
    .map((f) => ({ ...f, values: f.values.filter((v) => v.uses > 0) }))
    .filter((f) => f.values.length > 0);
  const top = inUse.slice(0, TAGGED_FILTER_FACETS);
  const creator = inUse.find((f) => f.key === NAMED_PEOPLE_FACET && !top.includes(f));
  const filterFacets = creator ? [...top, creator] : top;
  const valueIds = Object.values(filters);
  const narrowed = debounced !== '' || valueIds.length > 0;
  const [page, setPage] = useState(1);
  // A new search, filter or order starts again from the first page.
  const scope = JSON.stringify([sort, dir, debounced, valueIds]);
  useEffect(() => setPage(1), [scope]);
  const table = useQuery(trpc.tags.tagged.queryOptions({ sort, dir, q: debounced || null, valueIds, subclips, page }, { placeholderData: (p) => p }));
  const rows = table.data?.rows ?? [];
  const facets = table.data?.facets ?? [];
  const total = table.data?.total ?? 0;
  const pageSize = table.data?.pageSize ?? 1;
  const pages = pageCount(total, pageSize);
  const ratingTargets = useMemo(() => rows.flatMap((r) => [
    { kind: 'asset' as const, id: r.id },
    ...r.subclips.map((sc) => ({ kind: 'snippet' as const, id: sc.id })),
  ]), [rows]);
  const ratings = useRatings(ratingTargets);
  const stars = (kind: 'asset' | 'snippet', id: string) => (
    <td><StarRating rating={ratings.ratingOf({ kind, id })} onRate={(n) => ratings.rate({ kind, id }, n)} /></td>
  );
  // A page left past the end (clips hidden or untagged elsewhere) steps back to the last one.
  useEffect(() => {
    if (table.data && page > pages) setPage(pages);
  }, [table.data, page, pages]);

  const toggle = (id: string) => {
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** A category cell: its values, and the pencil that edits them on this clip or subclip. */
  const cell = (f: { id: string; label: string }, target: EditTarget, held: { label: string }[] | undefined) => (
    <td key={f.id} className="tagged-facet cell-host">
      <span className="row tagged-cell">
        <span className="tagged-clamp">{held?.map((t) => t.label).join(' · ')}</span>
        <button type="button" className="cell-edit" title={`Edit ${f.label}`} aria-label={`Edit ${f.label} for ${target.name}`}
          onClick={(e) => { e.stopPropagation(); setEditing({ target, facetId: f.id }); }}>
          <Icon name="pencil" />
        </button>
      </span>
    </td>
  );

  /** A header that sorts on its column; clicked again, it flips. The arrow marks the sorted column. */
  const header = (label: string, key: Sort, className = '') => (
    <th key={key} className={className}>
      <button type="button" className={`th-sort${sort === key ? ' on' : ''}`}
        onClick={() => {
          if (sort === key) setDir(dir === 'desc' ? 'asc' : 'desc');
          else {
            setSort(key);
            setDir(TEXT_SORTS.includes(key) || facets.some((f) => f.id === key) ? 'asc' : 'desc');
          }
        }}>
        {label}{sort === key ? (dir === 'desc' ? ' ↓' : ' ↑') : ''}
      </button>
    </th>
  );

  return (
    <div className="tagged">
      <div className="row lib-filters">
        <label className="field lib-search">
          <svg className="filter-glass" viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.5" /><path d="m10.5 10.5 3 3" /></svg>
          <input value={q} placeholder="Search names, tags, spoken words, descriptions…" aria-label="Search tagged clips" onChange={(e) => setQ(e.target.value)} />
        </label>
        {filterFacets.map((f) => (
          <label key={f.id} className={`field tagged-filter${filters[f.id] ? ' on' : ''}`}>
            <select value={filters[f.id] ?? ''} aria-label={`Filter by ${f.label}`}
              onChange={(e) => {
                const { [f.id]: _, ...rest } = filters;
                setFilters(e.target.value ? { ...rest, [f.id]: e.target.value } : rest);
              }}>
              <option value="">{f.key === NAMED_PEOPLE_FACET ? 'Creator' : f.label} · Any</option>
              {f.values.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </label>
        ))}
        {total > 0 && <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />}
      </div>
      <div className="card lib-table imports-table">
        <table className="tbl">
          <thead>
            <tr>
              <th className="check-cell">
                <input type="checkbox" aria-label="Select all"
                  checked={rows.length > 0 && rows.every((r) => picked.has(r.id))}
                  onChange={(e) => setPicked(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} />
              </th>
              <th className="thumb-cell" />
              {header('Asset', 'name')}
              {header('Length', 'length', 'n')}
              <th>Rating</th>
              {facets.map((f) => header(f.label, f.id, 'tagged-facet'))}
              {header('Subclips', 'snippets', 'n')}
              {header('Uploaded', 'created')}
            </tr>
          </thead>
          <tbody>
            {rows.flatMap((r) => [
              <tr key={r.id} className={`imports-row${subclips ? ' parent-row' : ''}${picked.has(r.id) ? ' lib-picked' : ''}`} onClick={() => onOpen(r.id)}>
                <td className="check-cell" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" aria-label={`Select ${r.name}`} checked={picked.has(r.id)} onChange={() => toggle(r.id)} />
                </td>
                <td className="thumb-cell">{r.posterUrl ? <img className="lib-thumb" src={r.posterUrl} alt="" /> : <span className="lib-thumb empty-poster" />}</td>
                <td className="tagged-name">
                  <span className="row lib-name-row">
                    <b className="lib-name" title={r.name}>{r.name}</b>
                    <button type="button" className="name-edit" title="Rename" aria-label={`Rename ${r.name}`}
                      onClick={(e) => { e.stopPropagation(); setRenaming({ id: r.id, name: r.name }); }}>
                      <Icon name="pencil" />
                    </button>
                  </span>
                </td>
                <td className="n mono nowrap">{r.durationMs ? `${Math.round(r.durationMs / 1000)} s` : ''}</td>
                {stars('asset', r.id)}
                {facets.map((f) => cell(f, { kind: 'asset', id: r.id, name: r.name }, r.tags[f.id]))}
                <td className="n mono">{r.snippets || ''}</td>
                <td className="sub nowrap">{fmtWhen(r.createdAt)}</td>
              </tr>,
              ...r.subclips.map((sc) => (
                <tr key={sc.id} className="imports-row subclip-row" onClick={() => onOpen(r.id, sc.id)}>
                  <td className="check-cell" />
                  <td className="thumb-cell">{sc.posterUrl ? <img className="lib-thumb" src={sc.posterUrl} alt="" /> : <span className="lib-thumb empty-poster" />}</td>
                  <td className="tagged-name"><b className="lib-name" title={sc.name}>{sc.name}</b></td>
                  <td className="n mono nowrap">{`${Math.round(sc.durationMs / 1000)} s`}</td>
                  {stars('snippet', sc.id)}
                  {facets.map((f) => cell(f, { kind: 'snippet', id: sc.id, name: `${sc.name} · ${r.name}` }, sc.tags[f.id]))}
                  <td />
                  <td />
                </tr>
              )),
            ])}
            {table.data && rows.length === 0 && <tr><td colSpan={7 + facets.length} className="sub creator-empty">{narrowed ? 'Nothing matches.' : 'Nothing tagged yet.'}</td></tr>}
          </tbody>
        </table>
      </div>
      {total > 0 && <div className="row pager-foot"><Pager page={page} pageSize={pageSize} total={total} onPage={setPage} /></div>}
      {table.isError && <div className="card pad error" role="alert">{table.error.message}</div>}
      {editing && (() => {
        const facet = facets.find((f) => f.id === editing.facetId);
        if (!facet) return null;
        const { target } = editing;
        const held = target.kind === 'asset'
          ? rows.find((r) => r.id === target.id)?.tags[facet.id]
          : rows.flatMap((r) => r.subclips).find((sc) => sc.id === target.id)?.tags[facet.id];
        return (
          <CellTagsModal target={target} facet={facet} held={held ?? []}
            offered={brandFacets.data?.find((f) => f.id === facet.id)?.values ?? []}
            canConfigure={me.active?.role === 'owner'} onClose={() => setEditing(null)} />
        );
      })()}
      {renaming && <RenameClipModal assetId={renaming.id} name={renaming.name} onClose={() => setRenaming(null)} />}
    </div>
  );
}
